import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  InvestorStatementBatch,
  InvestorStatementItem,
} from '../../../models/admin/InvestorStatement';
import StatementReviewOverlays from './StatementReviewOverlays';
import StatementReviewRow from './StatementReviewRow';
import { useStatementReviewActions } from './useStatementReviewActions';

type ToastFn = (message: string, type: 'error' | 'warning' | 'info' | 'success') => void;

interface StatementReviewListProps {
  batch: InvestorStatementBatch;
  onItemsChange: (items: InvestorStatementItem[]) => void;
  onShowToast: ToastFn;
}

const StatementReviewList: React.FC<StatementReviewListProps> = ({
  batch,
  onItemsChange,
  onShowToast,
}) => {
  const {
    busyItemId,
    sendPromptItemId,
    composeItemId,
    replaceWarningItemId,
    handleView,
    handleConfirm,
    handleSkip,
    declineSend,
    acceptSend,
    closeCompose,
    markSent,
    cancelReplace,
    confirmReplace,
  } = useStatementReviewActions(batch.items, onItemsChange, onShowToast);

  const promptItem = batch.items.find((i) => i.externalId === sendPromptItemId) || null;
  const composeItem = batch.items.find((i) => i.externalId === composeItemId) || null;
  const replaceWarningItem = batch.items.find((i) => i.externalId === replaceWarningItemId) || null;

  if (batch.items.length === 0) {
    return (
      <Box sx={{ color: 'text.secondary' }}>
        {batch.noActivityReason || 'No entities had any activity as at this date.'}
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }} data-testid="statement-review-list">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Investment Entity</TableCell>
            <TableCell>Investor</TableCell>
            <TableCell align="right">Paid In</TableCell>
            <TableCell align="right">Paid Out</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {batch.items.map((item) => (
            <StatementReviewRow
              key={item.externalId}
              item={item}
              busy={busyItemId === item.externalId}
              onView={(i) => void handleView(i)}
              onConfirm={(i) => void handleConfirm(i)}
              onSkip={(i) => void handleSkip(i)}
            />
          ))}
        </TableBody>
      </Table>

      <StatementReviewOverlays
        replaceWarningItem={replaceWarningItem}
        promptItem={promptItem}
        composeItem={composeItem}
        onCancelReplace={cancelReplace}
        onConfirmReplace={confirmReplace}
        onDeclineSend={declineSend}
        onAcceptSend={acceptSend}
        onCloseCompose={closeCompose}
        onSent={markSent}
        onShowToast={onShowToast}
      />
    </Box>
  );
};

export default StatementReviewList;
